import { observer } from "mobx-react-lite"
import Layout from "../components/layout/Layout"
import { Button, Divider, Form, Input, Popover, Select, Space, Spin, message } from "antd";
import { useStore } from "../stores/root-store-context";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { validateMessages } from "../helpers/validationMessages";
import { useTranslation } from "react-i18next";
import { Divide, Italic } from "lucide-react";
import i18n from "../i18n";
import { InfoCircleOutlined } from "@ant-design/icons";

type RegisterItem = {
    title: string,
    description: string,
    tenant: string,
    countryId: number,
    adress: string,
    email: string,
    password: string,
    plan: string,
    tariffplaninterval: string,
}

const Register = observer(() => {
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const [registerItem, setRegisterItem] = useState<RegisterItem>();
    const [tenantError, setTenantError] = useState<string>("");
    const [emailError, setEmailError] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();
    const { applicationStore, apiStore, tariffPlansStore, countryStore, currencyExchangeRateStore } = useStore();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const tariff = searchParams.get('tariff'); // "comfort"
    const period = searchParams.get('period'); // "month"

    useEffect(() => {
        countryStore.GetAllAvailableCountries();
        if (applicationStore.siteConfig.isShowLanguage) {
            currencyExchangeRateStore.setCurrentCurrency("UAH");
        }
        tariffPlansStore.getTariffPlans();
    }, [countryStore, tariffPlansStore]);

    type PlanType = keyof typeof tariffPlansStore.globalPricing;
    type BillingPeriodType = keyof typeof tariffPlansStore.globalPricing[PlanType extends keyof typeof tariffPlansStore.globalPricing ? PlanType : never];

    const selectedPlan = form.getFieldValue('plan') as PlanType | undefined;
    const selectedPeriod = form.getFieldValue('billingPeriod') as BillingPeriodType | undefined;

    const priceUSD =
        selectedPlan && selectedPeriod
            ? tariffPlansStore.globalPricing[selectedPlan]?.[selectedPeriod] ?? null
            : null;

    const priceLOCAL =
        selectedPlan && selectedPeriod
            ? tariffPlansStore.localPricing[selectedPlan]?.[selectedPeriod] ?? null
            : null;

    useEffect(() => {
        form.setFieldsValue({
            plan: tariff ?? "basic",
            billingPeriod: period ?? "month"
        });
    }, []);


    const customizeRequiredMark = (label: React.ReactNode, { required }: { required: boolean }) => (
        <>
            <span style={{ fontWeight: 500 }}>{label}</span>
        </>
    );

    async function handleSubmit() {
        setIsLoading(true)
        const title = registerItem?.title;
        const description = registerItem?.description;
        const tenant = registerItem?.tenant;
        const countryId = registerItem?.countryId;
        const adress = registerItem?.adress;
        const email = registerItem?.email
        const password = registerItem?.password;
        const tariffplanId = registerItem?.plan == "basic" ? 1 : registerItem?.plan == "comfort" ? 6 : 7;
        const tariffplaninterval = registerItem?.tariffplaninterval;
        try {
            const response = await apiStore.CommonApiClient.companyCreate(title as string, description as string, tenant as string, countryId as number, adress as string, tariffplanId as number, tariffplaninterval as string);
            console.log("response.id");
            console.log(response);
            const response2 = await apiStore.CommonApiClient.accountRegister(
                email as string,
                password as string,
                "" as string,
                "" as string,
                response.id,
                "owner"
            );
            message.open({
                type: 'success',
                content: t('Company register successful!'),
                key: 'checkingTenant'
            });
            navigate("/login");
        } catch (error) {
            message.open({
                type: 'error',
                content: t('Unexpected error!') + error,
                key: 'checkingTenant'
            });
        } finally {
            setIsLoading(false)
        }
    }

    const validateTenant = async (tenant: string) => {
        message.open({
            type: 'loading',
            content: t('Checking tenant...'),
            key: 'checkingTenant'
        });

        const res = await applicationStore.checkCompany(tenant);

        if (!res) {
            message.destroy("checkingTenant");
            message.open({
                type: 'success',
                content: t('Tenant is free!'),
                key: 'checkingTenant'
            });
            return true;
        } else {
            message.destroy("checkingTenant");
            message.open({
                type: 'error',
                content: t('Tenant already using!'),
                key: 'checkingTenant'
            });
            return false;
        }
    }

    const validateEmail = async (email: string) => {
        message.open({
            type: 'loading',
            content: t('Checking email...'),
            key: 'checkingTenant'
        });
        const res = await applicationStore.checkEmail(email);

        if (!res) {
            message.destroy("checkingTenant");
            message.open({
                type: 'success',
                content: t('Email is free!'),
                key: 'checkingTenant'
            });
            return true;
        } else {
            message.destroy("checkingTenant");
            message.open({
                type: 'error',
                content: t('Email already using!'),
                key: 'checkingTenant'
            });
            return false;
        }
    }

    const countryOptions = useMemo(
        () => countryStore.toOptions(i18n?.language || "en", { withFlags: true }),
        [i18n?.language, countryStore.availableCountries.length]
    );

    return (
        <Layout headerStyle={1} footerStyle={2} >
            <div>
                <section className="contact-section fix section-padding">
                    <div className="container">
                        <div className="contact-wrapper-2">
                            <div className="col-lg-12">
                                <div className="contact-content">
                                    <h2>{t('loginPrompt')}</h2>
                                    <p>
                                        {t('fillRequiredInfo')}
                                    </p>
                                    <Spin spinning={isLoading}>
                                        <Form
                                            className="contact-form-items"
                                            form={form}
                                            variant='outlined'
                                            layout="vertical"
                                            labelCol={{ span: 24 }}
                                            onValuesChange={(changedValues, allValues) => setRegisterItem(allValues)}
                                            validateMessages={validateMessages}
                                            requiredMark={customizeRequiredMark}
                                            autoComplete="off"
                                            onFinish={async (values) => {
                                                console.log("values");
                                                console.log(values);
                                                handleSubmit();
                                            }}
                                            onFinishFailed={(errorInfo) => {
                                                console.log("onFinishFailed");
                                                console.log(errorInfo);
                                                if (errorInfo.errorFields.length > 0) {
                                                    errorInfo.errorFields.forEach((field) => {
                                                        if (field.name[0] === "tenant") {
                                                            setTenantError(t('tenant is required!'));
                                                        }
                                                        if (field.name[0] === "email") {
                                                            setEmailError(t('email is required!'));
                                                        }
                                                    });
                                                }
                                            }}
                                            scrollToFirstError={{ block: 'center', inline: 'nearest' }}>
                                            <div className="col-lg-12 wow fadeInUp" data-wow-delay=".3s">
                                                <div className="form-clt">
                                                    <Form.Item<RegisterItem> name="title" style={{ width: "100%" }} rules={[{ required: true }]} >
                                                        <Input placeholder={t('Company title')} />
                                                    </Form.Item>
                                                </div>
                                            </div>
                                            <div className="col-lg-12 wow fadeInUp" data-wow-delay=".5s">
                                                <div className="form-clt">
                                                    <Form.Item<RegisterItem>
                                                        name="tenant"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: t('tenant is required!'),
                                                            },
                                                            {
                                                                validator: async (_, value) => {
                                                                    if (!value || value.length <= 3) {
                                                                        return Promise.resolve();
                                                                    }

                                                                    const isFree = await validateTenant(value);
                                                                    if (isFree) {
                                                                        return Promise.resolve();
                                                                    } else {
                                                                        return Promise.reject(new Error(t('Tenant already using!')));
                                                                    }
                                                                },
                                                            },
                                                        ]}
                                                    >
                                                        <Input placeholder={t('Site tenant')} />
                                                    </Form.Item>
                                                </div>
                                            </div>
                                            <Form.Item label={t("Country")} required style={{ marginBottom: 20 }}>
                                                <Space.Compact block className="country-compact">
                                                    <Form.Item<RegisterItem>
                                                        name="countryId"
                                                        noStyle
                                                        rules={[{ required: true, message: t("ChooseCountry") }]}
                                                    >
                                                        <Select
                                                            showSearch
                                                            placeholder={t("Country")}
                                                            options={countryOptions}
                                                            optionFilterProp="name"
                                                            optionLabelProp="name"
                                                            filterOption={(input, option) => {
                                                                const value = input.toLowerCase();
                                                                const opt = option as any;

                                                                const country = countryStore.getById(opt.value);
                                                                if (!country) return false;

                                                                return (
                                                                    country.name.toLowerCase().includes(value) ||
                                                                    country.isoCode.toLowerCase().includes(value) ||
                                                                    country.iso3Code.toLowerCase().includes(value)
                                                                );
                                                            }}
                                                            getPopupContainer={(trigger) => trigger.parentElement || document.body}
                                                            allowClear
                                                            style={{ height: 60, width: "100%" }}
                                                        />
                                                    </Form.Item>

                                                    <Popover
                                                        trigger="click"
                                                        placement="topRight"
                                                        title={t("WhyAskingCountry")}
                                                        content={
                                                            <div style={{ maxWidth: 280, lineHeight: 1.5 }}>
                                                                <div>• {t("PaymentSystemLocalization")}</div>
                                                                <div>• {t("AggressionSubject")}</div>
                                                            </div>
                                                        }
                                                        getPopupContainer={(trigger) => trigger.parentElement || document.body}
                                                    >
                                                        <Button
                                                            type="default"
                                                            aria-label={t("WhyAskingCountry")}
                                                            icon={<InfoCircleOutlined />}
                                                            style={{ height: 60, aspectRatio: "1 / 1" }}
                                                            htmlType="button"
                                                        />
                                                    </Popover>
                                                </Space.Compact>
                                            </Form.Item>
                                            <div className="col-lg-12 wow fadeInUp" data-wow-delay=".5s">
                                                <div className="form-clt">
                                                    <Form.Item<RegisterItem> name="adress" style={{ width: "100%" }}>
                                                        <Input placeholder={t('Address')} />
                                                    </Form.Item>
                                                </div>
                                            </div>
                                            <div className="col-lg-12 wow fadeInUp" data-wow-delay=".5s">
                                                <div className="form-clt">
                                                    <Form.Item<RegisterItem> name="description" style={{ width: "100%" }}>
                                                        <Input placeholder={t('Description')} />
                                                    </Form.Item>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '16px', paddingBottom: 20 }}>
                                                <Form.Item
                                                    name="plan"
                                                    rules={[{ required: true, message: t("ChooseTariff") }]}
                                                    style={{ flex: 1, marginBottom: 0 }}
                                                >
                                                    <Select placeholder={t("TariffName")} style={{ height: 60 }}>
                                                        <Select.Option value="basic">{t("Tariffs.Basic.Title")}</Select.Option>
                                                        <Select.Option value="comfort">{t("Tariffs.Comfort.Title")}</Select.Option>
                                                        <Select.Option value="infinity">{t("Tariffs.Infinity.Title")}</Select.Option>
                                                    </Select>
                                                </Form.Item>

                                                <Form.Item
                                                    name="billingPeriod"
                                                    rules={[{ required: true, message: t("ChoosePaymentPeriod") }]}
                                                    style={{ flex: 1, marginBottom: 0 }}
                                                >
                                                    <Select placeholder={t("PaymentPeriod")} style={{ height: 60 }}>
                                                        <Select.Option value="month">{t("Tariffs.MonthlyPlan")}</Select.Option>
                                                        <Select.Option value="year">{t("Tariffs.YearlyPlan")}</Select.Option>
                                                    </Select>
                                                </Form.Item>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: "center", minWidth: 140, height: 60, flexDirection: 'column' }}>
                                                    <strong>
                                                        {priceUSD != null ? `${priceUSD} $ / ${t(form.getFieldValue("billingPeriod"))}` : ""}
                                                    </strong>

                                                    {applicationStore.siteConfig.isShowLanguage && (
                                                        <>
                                                            <span>{t("or")}</span>
                                                            <strong>
                                                                {priceLOCAL != null ? `${priceLOCAL} ${t("UAH")} / ${t(form.getFieldValue("billingPeriod"))}` : ""}
                                                            </strong>
                                                        </>
                                                    )}
                                                </div>

                                            </div>
                                            <div className="col-lg-12 wow fadeInUp" data-wow-delay=".5s">
                                                <div className="form-clt">
                                                    <Form.Item<RegisterItem>
                                                        name="email"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: t('email is required!'),
                                                            },
                                                            {
                                                                type: "email",
                                                                message: t('Invalid email!'),
                                                            },
                                                            {
                                                                validator: async (_, value) => {
                                                                    if (!value || value.length <= 3) {
                                                                        return Promise.resolve();
                                                                    }

                                                                    const isFree = await validateEmail(value);
                                                                    if (isFree) {
                                                                        return Promise.resolve();
                                                                    } else {
                                                                        return Promise.reject(new Error(t('Email already using!')));
                                                                    }
                                                                },
                                                            },
                                                        ]}
                                                    >
                                                        <Input placeholder={t('Email')} />
                                                    </Form.Item>
                                                </div>
                                            </div>
                                            <div className="col-lg-12 wow fadeInUp" data-wow-delay=".5s">
                                                <div className="form-clt">
                                                    <Form.Item<RegisterItem> name="password" style={{ width: "100%" }} rules={[
                                                        {
                                                            required: true,
                                                            pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
                                                            message: t('passwordValidation')
                                                        }
                                                    ]} >
                                                        <Input placeholder={t('Password')} type="password" />
                                                    </Form.Item>
                                                </div>
                                            </div>
                                            <div className="col-lg-7 wow fadeInUp" data-wow-delay=".9s">
                                                <Form.Item>
                                                    <Button id="registerCompanyBtn" type="primary" className="theme-btn" htmlType="submit" style={{ height: 60 }} onClick={async () => {

                                                        try {
                                                            // await form.validateFields();
                                                            console.log("true")
                                                            // handleSubmit();
                                                        } catch (e) {
                                                            console.log("Not a valid form")
                                                        }
                                                        // console.log(res);
                                                        // handleSubmit()
                                                    }}>
                                                        {t('Register')} <i className="fa-solid fa-arrow-right-long" />
                                                    </Button>
                                                </Form.Item>
                                            </div>
                                        </Form>
                                    </Spin>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                {/*<< Map Section Start >>*/}
                <div className="map-section">
                    <div className="map-items">
                        <div className="googpemap">
                            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6678.7619084840835!2d144.9618311901502!3d-37.81450084255415!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad642b4758afc1d%3A0x3119cc820fdfc62e!2sEnvato!5e0!3m2!1sen!2sbd!4v1641984054261!5m2!1sen!2sbd" style={{ border: 0 }} allowFullScreen loading="lazy" />
                        </div>
                    </div>
                </div>
            </div >
        </Layout >
    )
})
export default Register;